
import { supabase } from '@/integrations/supabase/client';
import { debugVoteFlow } from '@/utils/debug';

/**
 * Reconcile poll vote counts to ensure accuracy
 */
export const reconcilePollVotes = async (pollId: string): Promise<void> => {
  try {
    debugVoteFlow('POLL_RECONCILIATION_START', { pollId });

    // Get poll statistics using the enhanced function
    const { data: pollStats, error: statsError } = await supabase
      .rpc('calculate_poll_statistics', { input_poll_id: pollId });

    if (statsError) {
      debugVoteFlow('POLL_RECONCILIATION_STATS_ERROR', { error: statsError });
      throw statsError;
    }

    // Update poll options counts
    const { error: updateError } = await supabase
      .rpc('update_poll_options_counts', { poll_id: pollId });

    if (updateError) {
      debugVoteFlow('POLL_RECONCILIATION_UPDATE_ERROR', { error: updateError });
      throw updateError;
    }

    // Update insight card if linked
    const { error: insightError } = await supabase
      .rpc('update_insight_card_stats', { input_poll_id: pollId });

    if (insightError) {
      debugVoteFlow('POLL_RECONCILIATION_INSIGHT_ERROR', { error: insightError });
      // Don't throw here as insight update is not critical
      console.warn('Failed to update insight card stats:', insightError);
    }

    debugVoteFlow('POLL_RECONCILIATION_SUCCESS', { 
      pollId, 
      totalVotes: (pollStats as any)?.total_votes || 0 
    });

  } catch (error) {
    debugVoteFlow('POLL_RECONCILIATION_ERROR', { error });
    console.error('Poll vote reconciliation failed:', error);
    throw error;
  }
};

/**
 * Reconcile all polls with recent vote activity
 */
export const reconcileRecentPollVotes = async (): Promise<void> => {
  try {
    debugVoteFlow('RECENT_POLLS_RECONCILIATION_START', {});

    // Get polls with recent vote activity (last 5 minutes)
    const recentCutoff = new Date(Date.now() - 5 * 60 * 1000).toISOString();

    const { data: recentVotedPolls, error: recentError } = await supabase
      .from('poll_votes')
      .select('poll_id')
      .gte('created_at', recentCutoff)
      .order('created_at', { ascending: false });

    if (recentError) {
      throw recentError;
    }

    // Also get anonymous votes
    const { data: recentAnonymousVotes, error: anonymousError } = await supabase
      .from('anonymous_poll_votes')
      .select('poll_id')
      .gte('created_at', recentCutoff)
      .order('created_at', { ascending: false });

    if (anonymousError) {
      throw anonymousError;
    }

    // Combine and deduplicate poll IDs
    const allRecentPollIds = [
      ...(recentVotedPolls?.map(v => v.poll_id) || []),
      ...(recentAnonymousVotes?.map(v => v.poll_id) || [])
    ];

    const uniquePollIds = [...new Set(allRecentPollIds)];

    debugVoteFlow('RECENT_POLLS_RECONCILIATION_FOUND', { 
      count: uniquePollIds.length 
    });

    // Reconcile each poll
    for (const pollId of uniquePollIds) {
      try {
        await reconcilePollVotes(pollId);
      } catch (error) {
        console.warn(`Failed to reconcile poll ${pollId}:`, error);
      }
    }

    debugVoteFlow('RECENT_POLLS_RECONCILIATION_SUCCESS', { 
      reconciledCount: uniquePollIds.length 
    });

  } catch (error) {
    debugVoteFlow('RECENT_POLLS_RECONCILIATION_ERROR', { error });
    console.error('Recent polls reconciliation failed:', error);
  }
};

/**
 * Fix all poll vote counts by reconciling all active polls
 */
export const fixAllPollVoteCounts = async (): Promise<void> => {
  try {
    debugVoteFlow('FIX_ALL_POLLS_START', {});

    // Get all active polls
    const { data: polls, error: pollsError } = await supabase
      .from('polls')
      .select('id')
      .eq('status', 'live');

    if (pollsError) {
      throw pollsError;
    }

    if (!polls || polls.length === 0) {
      debugVoteFlow('FIX_ALL_POLLS_NO_POLLS', {});
      return;
    }

    debugVoteFlow('FIX_ALL_POLLS_FOUND', { count: polls.length });

    // Reconcile each poll
    for (const poll of polls) {
      try {
        await reconcilePollVotes(poll.id);
      } catch (error) {
        console.warn(`Failed to reconcile poll ${poll.id}:`, error);
      }
    }

    debugVoteFlow('FIX_ALL_POLLS_SUCCESS', { 
      reconciledCount: polls.length 
    });

  } catch (error) {
    debugVoteFlow('FIX_ALL_POLLS_ERROR', { error });
    console.error('Fix all polls failed:', error);
    throw error;
  }
};
